import { useEffect, useState, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import "./App.css";
import { PillarMark, WorkflowDiagram, ArchitectureDiagram, BlueprintCorner } from "./components/Diagrams";
import BootSequence from "./components/BootSequence";
import { useScrollProgress, useRAF } from "./hooks/useMotion";
import { useStripeCheckout } from "./hooks/useStripeCheckout";
import { useLenis } from "./design-tools/useLenis";
import { useGSAPReveal, useStaggerReveal } from "./design-tools/useGSAPReveal";
import ParticleField from "./design-tools/ParticleField";
import GlowLine from "./design-tools/GlowLine";
import VignetteOverlay from "./design-tools/VignetteOverlay";
import NoiseTexture from "./design-tools/NoiseTexture";
import { PALETTE, GLOW, SHADOW, MOTION } from "./design-tools/tokens";
import heroImage from "./assets/Seedream-4_00012_.png";
import starsImg from "./assets/stars.png";

/* ─────────────────────────────────────────────
   SCROLL PROGRESS BAR
───────────────────────────────────────────── */
function ScrollProgress() {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const update = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(max > 0 ? (window.scrollY / max) * 100 : 0);
    };
    window.addEventListener("scroll", update, { passive: true });
    return () => window.removeEventListener("scroll", update);
  }, []);
  return (
    <div
      className="scroll-progress"
      style={{ width: `${progress}%` }}
      aria-hidden="true"
    />
  );
}

/* ─────────────────────────────────────────────
   NAV
───────────────────────────────────────────── */
function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = ["About", "Services", "AI", "Web Dev", "APS", "Contact"];

  return (
    <>
      <nav
        data-testid="nav"
        style={{
          position: "fixed",
          top: 0, left: 0, right: 0,
          zIndex: 50,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: scrolled ? "14px 56px" : "22px 56px",
          background: scrolled ? "rgba(13,15,20,0.92)" : "rgba(13,15,20,0.55)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          borderBottom: scrolled ? `1px solid ${PALETTE.border}` : "1px solid transparent",
          transition: `all ${MOTION.medium} ${MOTION.ease}`,
        }}
      >
        <Link to="/" style={{ display: "flex", flexDirection: "column", gap: 2, textDecoration: "none" }} aria-label="Back to the Digital Billions Group hub">
          <span style={{ fontFamily: "var(--font-display)", fontWeight: 500, fontSize: "15px", letterSpacing: "0.06em", color: PALETTE.white, lineHeight: 1 }}>
            Digital Billions Group
          </span>
          <span style={{ fontFamily: "var(--font-mono)", fontSize: "8px", letterSpacing: "0.35em", textTransform: "uppercase", color: PALETTE.teal500 }}>
            Global Digital Innovation
          </span>
        </Link>

        <div className="desktop-nav" style={{ display: "flex", gap: 36, alignItems: "center" }}>
          {links.map((item) => (
            <a key={item} href={`#${item.toLowerCase().replace(" ", "-")}`} className="nav-link">
              {item}
            </a>
          ))}
        </div>

        <button className={`hamburger${open ? " open" : ""}`} onClick={() => setOpen(!open)} aria-label="Toggle menu">
          <span /><span /><span />
        </button>
      </nav>

      <div className={`mobile-menu${open ? " open" : ""}`} role="dialog">
        {links.map((item) => (
          <a key={item} href={`#${item.toLowerCase().replace(" ", "-")}`} className="nav-link" onClick={() => setOpen(false)}>
            {item}
          </a>
        ))}
        <span style={{ fontFamily: "var(--font-mono)", fontSize: "9px", letterSpacing: "0.3em", textTransform: "uppercase", color: PALETTE.whiteFaint, marginTop: 20 }}>
          ◈ Digital Billions Group
        </span>
      </div>
    </>
  );
}

/* ─────────────────────────────────────────────
   HERO — video background, telemetry corners
───────────────────────────────────────────── */
function Hero() {
  const ref   = useRef();
  const time  = useRAF();
  const scrollP = useScrollProgress(ref);

  return (
    <section
      ref={ref}
      id="hero"
      data-testid="hero"
      className="hero-section"
      style={{
        minHeight: "100svh",
        position: "relative",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        padding: "120px var(--pad-x) 100px",
        background: PALETTE.ink,
      }}
    >
      {/* ── Background image (Seedream) ── */}
      <div
        className="hero-video"
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          backgroundImage: `url(${heroImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          opacity: 0.32,
          zIndex: 0,
          pointerEvents: "none",
        }}
        aria-hidden="true"
      />

      {/* ── Gradient overlay ── */}
      <div style={{
        position: "absolute", inset: 0, zIndex: 1, pointerEvents: "none",
        background: `
          radial-gradient(ellipse at 70% 30%, rgba(127,161,194,0.07), transparent 55%),
          linear-gradient(180deg, rgba(13,15,20,0.55) 0%, rgba(13,15,20,0.82) 80%, ${PALETTE.ink} 100%)
        `,
      }} aria-hidden="true" />

      {/* ── Bottom vignette ── */}
      <div style={{
        position: "absolute", bottom: 0, left: 0, right: 0, zIndex: 5, pointerEvents: "none",
        height: "220px",
        background: `linear-gradient(to bottom, transparent 0%, rgba(13,15,20,0.55) 40%, ${PALETTE.ink} 100%)`,
      }} aria-hidden="true" />

      <ParticleField />
      <VignetteOverlay strength={0.6} color={PALETTE.ink} />

      {/* Scan line */}
      <div className="scan-line" />

      {/* ── Blueprint corners ── */}
      {[
        { pos: "tl", top: 118, left: "var(--pad-x)" },
        { pos: "tr", top: 118, right: "var(--pad-x)" },
        { pos: "bl", bottom: 56, left: "var(--pad-x)" },
        { pos: "br", bottom: 56, right: "var(--pad-x)" },
      ].map(({ pos, top, bottom, left, right }) => (
        <div key={pos} style={{ position: "absolute", top, bottom, left, right, zIndex: 3 }}>
          <BlueprintCorner position={pos} ink={PALETTE.teal600} opacity={0.65} size={40} />
        </div>
      ))}

      {/* ── Telemetry readouts ── */}
      <div className="hero-telemetry hero-telemetry-tl" style={{
        position: "absolute", top: 126, left: "calc(var(--pad-x) + 52px)",
        color: PALETTE.teal700, zIndex: 3,
        fontFamily: "var(--font-mono)", fontSize: "9px", letterSpacing: "0.28em",
        textTransform: "uppercase", lineHeight: 1.8,
      }}>
        <div>N 40°43′ &nbsp; W 74°00′</div>
        <div style={{ opacity: 0.65 }}>◌ &nbsp; ATELIER 01 &nbsp; ◌ &nbsp; DIGITAL BILLIONS</div>
      </div>
      <div className="hero-telemetry hero-telemetry-tr" style={{
        position: "absolute", top: 126, right: "calc(var(--pad-x) + 52px)",
        color: PALETTE.teal700, zIndex: 3, textAlign: "right",
        fontFamily: "var(--font-mono)", fontSize: "9px", letterSpacing: "0.28em",
        textTransform: "uppercase", lineHeight: 1.8,
      }}>
        <div>T+{time.toFixed(2)}s</div>
        <div style={{ opacity: 0.65 }}>◌ &nbsp; LIVE &nbsp; ◌ &nbsp; SIGNAL ACTIVE</div>
      </div>

      {/* ── Bottom telemetry ── */}
      <div className="hero-telemetry hero-telemetry-bl" style={{
        position: "absolute", bottom: 66, left: "calc(var(--pad-x) + 52px)",
        color: PALETTE.teal700, zIndex: 3,
        fontFamily: "var(--font-mono)", fontSize: "9px", letterSpacing: "0.24em",
        textTransform: "uppercase",
      }}>
        SYS:READY &nbsp; ◌ &nbsp; MMXXVI
      </div>
      <div className="hero-telemetry hero-telemetry-br" style={{
        position: "absolute", bottom: 66, right: "calc(var(--pad-x) + 52px)",
        color: PALETTE.teal700, zIndex: 3, textAlign: "right",
        fontFamily: "var(--font-mono)", fontSize: "9px", letterSpacing: "0.24em",
        textTransform: "uppercase",
      }}>
        DBG-HQ &nbsp; ◌ &nbsp; GLOBAL
      </div>

      {/* ── Decorative vertical line ── */}
      <div style={{
        position: "absolute", top: 0, left: "var(--pad-x)",
        width: "1px", height: "100%",
        background: `linear-gradient(180deg, transparent 0%, ${PALETTE.teal800} 30%, transparent 100%)`,
        opacity: 0.5, zIndex: 2,
      }} aria-hidden="true" />

      {/* ── Schematic grid deco ── */}
      <div style={{
        position: "absolute", top: "22%", right: "7%",
        width: "340px", height: "340px", zIndex: 2, opacity: 0.1, pointerEvents: "none",
      }} aria-hidden="true">
        <svg width="340" height="340" viewBox="0 0 340 340">
          {[0,1,2,3,4].map(i => (
            <line key={`h${i}`} x1="0" y1={i*85} x2="340" y2={i*85} stroke={PALETTE.teal500} strokeWidth="0.6" strokeDasharray="4 7" />
          ))}
          {[0,1,2,3,4].map(i => (
            <line key={`v${i}`} x1={i*85} y1="0" x2={i*85} y2="340" stroke={PALETTE.teal500} strokeWidth="0.6" strokeDasharray="4 7" />
          ))}
          <circle cx="170" cy="170" r="110" fill="none" stroke={PALETTE.teal500} strokeWidth="0.6" strokeDasharray="3 6" />
          <circle cx="170" cy="170" r="65"  fill="none" stroke={PALETTE.teal500} strokeWidth="0.5" />
          <circle cx="170" cy="170" r="4"   fill={PALETTE.teal500} />
        </svg>
      </div>

      {/* ── Copy ── */}
      <div className="hero-copy animate-hero-0" style={{
        position: "relative",
        zIndex: 4,
        transform: `translateY(${scrollP * -30}px)`,
        opacity: 1 - scrollP * 0.7,
      }}>
        <p className="teal-label animate-hero-0" style={{ marginBottom: 40 }}>
          ◈ &nbsp; DIGITAL BILLIONS GROUP &nbsp; ◈ &nbsp; DISTINGUISHED DEVELOPMENT &amp; INNOVATION
        </p>

        <h1 className="animate-hero-1" style={{
          fontFamily: "var(--font-display)", fontWeight: 300,
          fontSize: "clamp(52px, 9vw, 124px)", lineHeight: 0.9,
          letterSpacing: "-0.025em", color: PALETTE.white, marginBottom: 10,
        }}>
          Innovation,
        </h1>
        <h1 className="animate-hero-2" style={{
          fontFamily: "var(--font-display)", fontWeight: 300, fontStyle: "italic",
          fontSize: "clamp(52px, 9vw, 124px)", lineHeight: 0.9,
          letterSpacing: "-0.025em", color: PALETTE.teal400, marginBottom: 56,
          textShadow: GLOW.text,
        }}>
          Elegantly Deployed.
        </h1>

        <p className="animate-hero-3" style={{
          fontFamily: "var(--font-body)", fontWeight: 300, fontSize: "17px",
          lineHeight: 1.85, color: PALETTE.whiteMid, maxWidth: "560px", marginBottom: 56,
          letterSpacing: "0.01em",
        }}>
         Where intelligent design meets refined technology. We are a distinguished development team working across the globe to help founders, enterprises, and
          creators bring their most ambitious visions to life — building the critical
          infrastructure that powers growth, at scale, worldwide.
        </p>

        <div className="animate-hero-4" style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
          <a href="#services" className="btn-teal">
            <span>Explore Our Work</span>
            <span aria-hidden="true">↓</span>
          </a>
          <a href="#contact" className="btn-outline">
            <span>Start a Conversation</span>
            <span aria-hidden="true">→</span>
          </a>
        </div>
      </div>

      {/* ── Scroll indicator ── */}
      <div className="hero-side-label animate-hero-0" aria-hidden="true" style={{ zIndex: 4 }}>
        <div style={{ writingMode: "vertical-rl", fontFamily: "var(--font-mono)", fontSize: "9px", letterSpacing: "0.3em", textTransform: "uppercase", color: PALETTE.teal800 }}>
          Scroll to explore
        </div>
        <div style={{ width: "1px", height: "60px", background: `linear-gradient(180deg, ${PALETTE.teal700}, transparent)`, position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "40%", background: PALETTE.teal500, animation: "scrollDot 2.4s cubic-bezier(0.5,0,0.5,1) infinite" }} />
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   ABOUT
───────────────────────────────────────────── */
function About() {
  const ref = useRef();
  const p   = useScrollProgress(ref);

  return (
    <section ref={ref} id="about" data-testid="about" className="section-pad" style={{
      background: `linear-gradient(180deg, ${PALETTE.ink} 0%, #15171F 100%)`,
      position: "relative", overflow: "hidden",
    }}>
      <VignetteOverlay strength={0.3} color={PALETTE.ink} />
      <div className="two-col" style={{ position: "relative", zIndex: 2 }}>
        {/* Left */}
        <div className="gsap-reveal">
          <p className="teal-label" style={{ marginBottom: 32 }}>§ &nbsp; About Us</p>
          <GlowLine style={{ marginBottom: 32, maxWidth: 40 }} />
          <h2 style={{ fontFamily: "var(--font-display)", fontWeight: 300, fontSize: "clamp(32px, 4vw, 52px)", lineHeight: 1.08, letterSpacing: "-0.01em", color: PALETTE.white }}>
            Bring Your<br />
            <em style={{ color: PALETTE.teal400, textShadow: GLOW.text }}>Vision to Life.</em>
          </h2>
          <svg width="200" height="200" viewBox="0 0 220 220" style={{ marginTop: 48, opacity: 0.75 }}>
            <circle cx="110" cy="110" r="80" fill="none" stroke={PALETTE.teal600} strokeWidth="0.8" />
            <circle cx="110" cy="110" r="55" fill="none" stroke={PALETTE.teal500} strokeWidth="0.6" strokeDasharray="2 3" opacity="0.6" />
            <circle cx="110" cy="110" r="30" fill="none" stroke={PALETTE.teal500} strokeWidth="0.6" />
            <circle cx="110" cy="110" r="3" fill={PALETTE.teal400} />
            <g transform={`rotate(${p * 60} 110 110)`}>
              <circle cx="190" cy="110" r="2" fill={PALETTE.teal500} />
              <circle cx="30" cy="110" r="2" fill={PALETTE.teal500} />
            </g>
            <g transform={`rotate(${-p * 40 + 45} 110 110)`}>
              <circle cx="165" cy="110" r="2" fill={PALETTE.teal400} />
            </g>
            <text x="110" y="22" textAnchor="middle" style={{ fontFamily: "var(--font-mono)", fontSize: 8, letterSpacing: "0.2em", fill: PALETTE.teal600 }}>SYSTEMA</text>
          </svg>
        </div>

        {/* Right */}
        <div className="gsap-reveal" style={{ paddingTop: 4 }} data-reveal-delay="120">
          <p style={{ fontFamily: "var(--font-body)", fontWeight: 300, fontSize: "17px", lineHeight: 1.85, color: PALETTE.whiteMid, marginBottom: 28 }}>
            Digital Billions Group is a distinguished development team focused on building
            impactful technology solutions for the world's most forward-thinking brands,
            founders, and organizations — from emerging ventures to enterprise-scale operations.
          </p>
          <p style={{ fontFamily: "var(--font-body)", fontWeight: 300, fontSize: "17px", lineHeight: 1.85, color: PALETTE.whiteMid, marginBottom: 28 }}>
            We help people bring their visions and dreams to life. We build critical
            infrastructure. And we serve clients across the globe — always with precision,
            always with purpose, always with a team that treats your project as if it were our own.
          </p>
          <p style={{
            fontFamily: "var(--font-display)", fontStyle: "italic", fontWeight: 400,
            fontSize: "20px", lineHeight: 1.6, color: PALETTE.teal400,
            borderLeft: `2px solid ${PALETTE.teal700}`, paddingLeft: "20px", marginTop: 40,
            textShadow: GLOW.text,
          }}>
            The work speaks. The results endure.
          </p>

          {/* Stats */}
          <div className="stagger-container" style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 24, marginTop: 64, paddingTop: 32, borderTop: `1px solid ${PALETTE.border}` }}>
            {[
              { n: "05", l: "Pillars of practice" },
              { n: "∞",  l: "Iterations refined" },
              { n: "01", l: "Unifying philosophy" },
            ].map((s, i) => (
              <div key={i} className="stagger-child stat-card">
                <div style={{ fontFamily: "var(--font-display)", fontWeight: 300, fontSize: 48, color: PALETTE.teal400, lineHeight: 1, textShadow: GLOW.text }}>
                  {s.n}
                </div>
                <div className="mono-tiny" style={{ marginTop: 8, color: PALETTE.teal600 }}>{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   SERVICES / OFFERINGS
───────────────────────────────────────────── */
const offerings = [
  { number: "01", kind: "ai",        glyph: "◎", title: "Intelligent Automation",        description: "Custom automation systems engineered around how your business actually operates. Built for teams serious about efficiency, speed, and scale — not just novelty." },
  { number: "02", kind: "vending",   glyph: "◫", title: "Digital Vending",                description: "From luxury retail kiosks to themed entertainment experiences, our vending systems turn passive transactions into memorable moments. Precision engineering meets brand strategy." },
  { number: "03", kind: "media",     glyph: "◈", title: "Multi‑Media Publishing",          description: "We amplify artists, creators, and performance brands through our global publishing network. Creation and distribution handled with the professionalism your craft deserves." },
  { number: "04", kind: "marketing", glyph: "◉", title: "Digital Marketing",               description: "White-glove marketing built for brands that expect more from their investment. Analytics meets artistry — Meta, TikTok, Google, and bespoke SEO campaigns engineered to convert." },
  { number: "05", kind: "app",       glyph: "◉", title: "Application Development",         description: "Polished static sites to enterprise-grade cross-platform platforms. Every solution is architected for performance, designed for longevity, and built to carry the weight of real business." },
];

function OfferingRow({ o, i }) {
  const ref     = useRef();
  const p       = useScrollProgress(ref);
  const t       = useRAF();
  const [hovered, setHovered] = useState(false);

  return (
    <div
      ref={ref}
      className="offering-row"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        opacity: 0.25 + Math.min(1, p * 2.5) * 0.75,
        transform: `translateX(${(1 - Math.min(1, p * 2.5)) * -20}px)`,
        transition: "opacity 0.6s var(--ease), transform 0.6s var(--ease)",
      }}
    >
      <div className="offering-inner">
        <div className="offering-pillar" style={{ display: "flex", justifyContent: "center", transform: hovered ? "scale(1.08)" : "scale(1)", transition: `transform ${MOTION.medium} ${MOTION.ease}`, filter: hovered ? `drop-shadow(0 0 10px ${PALETTE.teal600})` : "none" }}>
          <PillarMark kind={o.kind} size={100} t={t} ink={PALETTE.diagramInk} accent={PALETTE.teal400} />
        </div>
        <div className="offering-num" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <span style={{ fontFamily: "var(--font-mono)", fontSize: "9px", letterSpacing: "0.3em", color: PALETTE.teal700, textTransform: "uppercase" }}>{o.number}</span>
          <span style={{ fontSize: "18px", color: PALETTE.teal500, lineHeight: 1 }}>{o.glyph}</span>
        </div>
        <h3 style={{ fontFamily: "var(--font-display)", fontWeight: 400, fontSize: "clamp(20px, 2.5vw, 30px)", lineHeight: 1.15, letterSpacing: "-0.01em", color: hovered ? PALETTE.teal300 : PALETTE.white, transition: `color ${MOTION.fast} ease`, paddingTop: 4 }}>
          {o.title}
        </h3>
        <p className="offering-desc" style={{ fontFamily: "var(--font-body)", fontWeight: 300, fontSize: "15px", lineHeight: 1.8, color: PALETTE.whiteMid }}>
          {o.description}
        </p>
      </div>
    </div>
  );
}

function Services() {
  return (
    <section id="services" data-testid="services" className="section-pad" style={{ background: "#13151B", position: "relative" }}>
      <div className="offerings-header gsap-reveal">
        <div>
          <p className="teal-label" style={{ marginBottom: 20 }}>◈ &nbsp; What We Build</p>
          <h2 style={{ fontFamily: "var(--font-display)", fontWeight: 300, fontSize: "clamp(32px, 4vw, 56px)", lineHeight: 1, letterSpacing: "-0.01em", color: PALETTE.white }}>
            Our Services
          </h2>
        </div>
        <p className="sub-label" style={{ fontFamily: "var(--font-mono)", fontSize: "10px", letterSpacing: "0.15em", textTransform: "uppercase", color: PALETTE.teal600, maxWidth: "220px", textAlign: "right", lineHeight: 1.7 }}>
          Five pillars. One team. A single standard of excellence.
        </p>
      </div>
      <div>
        {offerings.map((o, i) => <OfferingRow key={o.number} o={o} i={i} />)}
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   ATMOSPHERIC QUOTE
───────────────────────────────────────────── */
function AtmosphericDivider() {
  const ref = useRef();
  const p   = useScrollProgress(ref);
  return (
    <section ref={ref} className="atm-divider section-pad" style={{ position: "relative", overflow: "hidden" }}>
      <ParticleField style={{ opacity: 0.35 }} />
      <VignetteOverlay strength={0.5} color="#171A21" />
      <div style={{ maxWidth: "900px", margin: "0 auto", textAlign: "center", position: "relative", zIndex: 2, transform: `translateY(${(p - 0.5) * -50}px)` }}>
        <p className="teal-label" style={{ marginBottom: 32 }}>◈ &nbsp; What We Believe</p>
        <GlowLine style={{ margin: "0 auto 40px", maxWidth: 120 }} animated />
        <p style={{ fontFamily: "var(--font-display)", fontWeight: 300, fontStyle: "italic", fontSize: "clamp(26px, 3.5vw, 46px)", lineHeight: 1.35, letterSpacing: "-0.01em", color: PALETTE.white }}>
          “It always seems impossible
          <span style={{ color: PALETTE.teal400, textShadow: GLOW.text }}> until it’s done.”</span>
        </p>
        <p className="mono-tiny" style={{ marginTop: 28, color: PALETTE.teal600 }}>
          — Nelson Mandela
        </p>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   AI SERVICES
   — 3 tiers (no $99 discovery here)
   — $99 appears only in the CTA band below
───────────────────────────────────────────── */
const aiPlans = [
  {
    tier: "01", glyph: "◉", name: "Starter Automation", price: "$299", cadence: "/ month", tag: "Most popular", featured: true,
    description: "Your first automation layer — a 24/7 system handling inquiries, bookings, and lead capture so your team can focus on the work that matters.",
    includes: ["Intelligent website chat assistant", "Automated appointment scheduling", "Lead capture and real-time notifications", "Core workflow automation", "Monthly performance monitoring"],
    cta: "Start My Automation", ctaSymbol: "→", stripeEnv: "REACT_APP_STRIPE_AI_STARTER",
  },
  {
    tier: "02", glyph: "◈", name: "Business Automation", price: "$799", cadence: "/ month", tag: "Full stack", featured: false,
    description: "End-to-end automation across your customer journey — communications, scheduling, and CRM working in concert, fully integrated.",
    includes: ["Multi-channel assistant (web + messaging)", "Automated booking and confirmation system", "Email and SMS follow-up sequences", "CRM integration and data sync", "Lead qualification workflows", "Monthly optimization review"],
    cta: "Launch My System", ctaSymbol: "→", stripeEnv: "REACT_APP_STRIPE_AI_BUSINESS",
  },
  {
    tier: "03", glyph: "◫", name: "Advanced Automation", price: "From $2,500", cadence: "/ month", tag: "Enterprise", featured: false,
    description: "Multi-department automation engineered around the specific architecture of your business. Built for organizations that have outgrown off-the-shelf tools.",
    includes: ["Custom intelligent customer service systems", "Sales pipeline and lead management automation", "Operations and fulfillment workflow automation", "Internal staff-facing automation tools", "Data processing, analytics, and reporting", "Dedicated engineering and optimization support"],
    cta: "Schedule a Consultation", ctaSymbol: "↗", stripeEnv: "REACT_APP_STRIPE_AI_ENTERPRISE",
  },
];

function PricingCard({ plan }) {
  const { checkout, loading } = useStripeCheckout();
  const [hovered, setHovered] = useState(false);
  const featured = plan.featured;
  const priceKey = plan.stripeEnv ? plan.stripeEnv.replace(/^REACT_APP_/, "") : null;

  const cardBg = featured
    ? "linear-gradient(160deg, #232838 0%, #181C28 100%)"
    : "linear-gradient(160deg, #1B1E26 0%, #14161D 100%)";

  return (
    <div
      className={`pricing-card-shimmer-border${featured ? " featured" : ""}`}
      style={{
        background: cardBg,
        border: `1px solid ${featured ? "rgba(157,180,202,0.35)" : "rgba(58,64,81,0.4)"}`,
        padding: "32px 24px 32px",
        display: "flex", flexDirection: "column",
        transition: `all ${MOTION.medium} ${MOTION.ease}`,
        boxShadow: hovered ? (featured ? "0 0 30px rgba(127,161,194,0.2), 0 0 60px rgba(127,161,194,0.08)" : GLOW.card) : "none",
        transform: hovered ? "translateY(-2px)" : "translateY(0)",
        position: "relative", overflow: "hidden",
        minHeight: 380,
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className={featured ? "pricing-card-hatch-featured" : "pricing-card-hatch"} />
      {featured && (
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "2px", background: `linear-gradient(90deg, transparent, rgba(127,161,194,0.6), transparent)` }} />
      )}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28, position: "relative", zIndex: 1 }}>
        <span style={{ fontFamily: "var(--font-mono)", fontSize: "9px", letterSpacing: "0.3em", color: PALETTE.teal700 }}>{plan.tier}</span>
        <span style={{
          fontFamily: "var(--font-mono)", fontSize: "9px", letterSpacing: "0.15em", textTransform: "uppercase",
          color: featured ? PALETTE.teal300 : PALETTE.teal500,
          border: `1px solid ${featured ? "rgba(157,180,202,0.4)" : "rgba(58,64,81,0.5)"}`,
          padding: "3px 9px",
          background: featured ? "rgba(127,161,194,0.08)" : "transparent",
        }}>
          {plan.tag}
        </span>
      </div>
      <div style={{ fontSize: "20px", color: featured ? PALETTE.teal400 : PALETTE.teal600, marginBottom: 14, position: "relative", zIndex: 1 }}>{plan.glyph}</div>
      <h3 style={{ fontFamily: "var(--font-display)", fontWeight: 400, fontSize: "22px", lineHeight: 1.15, color: PALETTE.white, marginBottom: 12, position: "relative", zIndex: 1 }}>
        {plan.name}
      </h3>
      <div style={{ marginBottom: 20, position: "relative", zIndex: 1 }}>
        <span style={{ fontFamily: "var(--font-display)", fontWeight: 300, fontStyle: "italic", fontSize: "32px", color: featured ? PALETTE.teal300 : PALETTE.white, letterSpacing: "-0.02em" }}>
          {plan.price}
        </span>
        <span style={{ fontFamily: "var(--font-mono)", fontSize: "10px", letterSpacing: "0.15em", color: PALETTE.teal700, marginLeft: 6 }}>
          {plan.cadence}
        </span>
      </div>
      <div style={{ height: "1px", background: `linear-gradient(90deg, ${featured ? "rgba(157,180,202,0.3)" : "rgba(58,64,81,0.4)"}, transparent)`, marginBottom: 20 }} />
      <p style={{ fontFamily: "var(--font-body)", fontWeight: 300, fontSize: "13px", lineHeight: 1.75, color: PALETTE.whiteMid, marginBottom: 24, flexGrow: 1, position: "relative", zIndex: 1 }}>
        {plan.description}
      </p>
      <ul style={{ listStyle: "none", padding: 0, margin: "0 0 0", display: "flex", flexDirection: "column", gap: 7, position: "relative", zIndex: 1 }}>
        {plan.includes.map((item, i) => (
          <li key={i} style={{ fontFamily: "var(--font-body)", fontWeight: 300, fontSize: "12px", lineHeight: 1.6, color: PALETTE.whiteMid, display: "flex", alignItems: "flex-start", gap: 8 }}>
            <span style={{ color: PALETTE.teal500, flexShrink: 0, marginTop: 2, fontSize: 10 }}>◌</span>
            {item}
          </li>
        ))}
      </ul>
      <button
        onClick={() => priceKey && checkout(priceKey)}
        disabled={loading || !priceKey}
        className={`pricing-cta-btn${featured ? " featured" : ""}`}
        style={{ marginTop: 28 }}
      >
        <span>{loading ? "Redirecting..." : plan.cta}</span>
        <span aria-hidden="true">{plan.ctaSymbol}</span>
      </button>
    </div>
  );
}

/* ─── CTA Band Button Components ─── */
function AICTAButtons() {
  const { checkout, loading } = useStripeCheckout();
  return (
    <>
      <button onClick={() => checkout("STRIPE_AI_STARTER")} disabled={loading} className="btn-teal">
        <span>{loading ? "Redirecting..." : "Start My Automation"}</span>
        <span aria-hidden="true">→</span>
      </button>
      <button onClick={() => checkout("STRIPE_AI_STRATEGY_CALL")} disabled={loading} className="btn-outline">
        <span>{loading ? "Redirecting..." : "Book $99 Intro Call"}</span>
        <span aria-hidden="true">◎</span>
      </button>
    </>
  );
}

function WebDevCTAButtons() {
  const { checkout, loading } = useStripeCheckout();
  return (
    <>
      <button onClick={() => checkout("STRIPE_DEV_PKG_A")} disabled={loading} className="btn-teal">
        <span>{loading ? "Redirecting..." : "Start My Project"}</span>
        <span aria-hidden="true">→</span>
      </button>
      <button onClick={() => checkout("STRIPE_DEV_STRATEGY_CALL")} disabled={loading} className="btn-outline">
        <span>{loading ? "Redirecting..." : "Book a Strategy Call"}</span>
        <span aria-hidden="true">◎</span>
      </button>
    </>
  );
}

function AIServices() {
  const ref = useRef();
  const p   = useScrollProgress(ref);
  const t   = useRAF();

  return (
    <section
      ref={ref}
      id="ai"
      data-testid="ai-services"
      className="section-pad"
      style={{
        background: `linear-gradient(160deg, #1B1E26 0%, #0D0F14 100%)`,
        position: "relative",
        overflow: "hidden",
      }}>
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          zIndex: 0,
          pointerEvents: "none",
          backgroundImage: `url(${starsImg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          opacity: 0.08,
          mixBlendMode: "screen",
        }}
      />
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          top: "15%",
          right: "-8%",
          width: "500px",
          height: "500px",
          background: `radial-gradient(circle, rgba(127,161,194,0.05), transparent 65%)`,
          pointerEvents: "none",
        }}
      />
      <VignetteOverlay strength={0.4} color="#0D0F14" />

      <div style={{ position: "absolute", top: 32, left: 32, opacity: 0.4, zIndex: 2 }}>
        <BlueprintCorner position="tl" ink={PALETTE.teal600} />
      </div>
      <div style={{ position: "absolute", top: 32, right: 32, opacity: 0.4, zIndex: 2 }}>
        <BlueprintCorner position="tr" ink={PALETTE.teal600} />
      </div>

      <div className="section-header-2col gsap-reveal" style={{ position: "relative", zIndex: 2 }}>
        <div>
          <p className="teal-label" style={{ marginBottom: 20 }}>
            ◉ &nbsp; Automation &amp; Intelligent Systems
          </p>
          <h2 style={{
            fontFamily: "var(--font-display)", fontWeight: 300,
            fontSize: "clamp(32px, 4vw, 56px)", lineHeight: 1,
            letterSpacing: "-0.01em", color: PALETTE.white,
          }}>
            Systems That Work<br />
            <em style={{ color: PALETTE.teal400, textShadow: GLOW.text }}>
              While You Do.
            </em>
          </h2>
        </div>
        <div>
          <p style={{
            fontFamily: "var(--font-body)", fontWeight: 300, fontSize: "17px",
            lineHeight: 1.85, color: PALETTE.whiteMid, marginBottom: 24,
          }}>
            The most productive businesses aren't the ones working hardest — they're the ones
            with the most intelligent infrastructure. We build automation systems that take
            the repetitive, the routine, and the time-consuming completely off your plate.
          </p>
          <p style={{
            fontFamily: "var(--font-display)", fontStyle: "italic", fontWeight: 400,
            fontSize: "18px", color: PALETTE.teal400,
            borderLeft: `2px solid ${PALETTE.teal700}`, paddingLeft: "20px",
            textShadow: GLOW.text,
          }}>
            Your business, running — even when you're not.
          </p>
        </div>
      </div>

      <div
        className="diagram-frame gsap-reveal"
        style={{
          position: "relative", zIndex: 2,
          opacity: Math.min(1, p * 1.5),
          transform: `translateY(${(1 - Math.min(1, p * 1.5)) * 36}px)`,
          transition: "opacity 0.5s ease, transform 0.5s ease",
        }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 24 }}>
          <span className="mono-tiny" style={{ color: PALETTE.teal500 }}>
            FIG. 01 · AGENTIC WORKFLOW SCHEMATIC
          </span>
          <span className="mono-tiny" style={{ color: PALETTE.teal600 }}>
            SCALE 1:1 &nbsp; ◌ &nbsp; LIVE
          </span>
        </div>
        <div style={{ height: 360 }}>
          <WorkflowDiagram width={1200} height={360} t={t} ink={PALETTE.diagramInk} accent={PALETTE.teal400} />
        </div>
      </div>

      {/* 3-tier grid */}
      <div style={{ position: "relative", zIndex: 2, marginBottom: 80 }}>
        <div className="pricing-grid pricing-grid-3 gsap-reveal" style={{ position: "relative", zIndex: 1 }}>
          {aiPlans.map((plan) => (
            <PricingCard key={plan.tier} plan={plan} />
          ))}
        </div>
      </div>

      {/* CTA band — this is where $99 lives */}
      <div className="cta-band gsap-reveal" style={{ position: "relative", zIndex: 2 }}>
        <div>
          <p className="teal-label" style={{ marginBottom: 16 }}>
            ◈ &nbsp; Ready to Add Automation to Your Business?
          </p>
          <p style={{
            fontFamily: "var(--font-body)", fontWeight: 300, fontSize: "15px",
            lineHeight: 1.75, color: PALETTE.whiteMid, maxWidth: "480px",
          }}>
            Not sure which system is right for you? Start with a $99 strategy call —
            we'll review your workflows and deliver a clear automation roadmap
            tailored to where you are today.
          </p>
        </div>
        <div className="cta-band-buttons">
          <AICTAButtons />
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   WEB DEV SERVICES
───────────────────────────────────────────── */
const webDevPlans = [
  {
    tier: "01", glyph: "◎", name: "Strategy Call", price: "$199", cadence: "One Time", tag: "Start here", featured: false,
    description: "A dedicated session with our engineering team — not a sales call. We map your goals, evaluate your technical landscape, and leave you with a concrete path forward.",
    includes: ["Current digital landscape review", "Technical requirements analysis", "Recommended architecture and stack", "Project scope, milestones, and timeline", "Yours to keep — regardless of next steps"],
    cta: "Book a Strategy Call", ctaSymbol: "→", stripeEnv: "REACT_APP_STRIPE_DEV_STRATEGY_CALL",
  },
  {
    tier: "02", glyph: "◉", name: "Launchpad", price: "$400", cadence: "/ month", tag: "Most popular", featured: true,
    description: "The fastest way to a polished, high-converting web presence. Custom-designed, mobile-first, and engineered to make the right impression — starting on day one.",
    includes: ["Custom-designed website, built to convert", "Fully mobile-responsive layout", "SEO-optimized site architecture", "Hosting, deployment, and DNS setup", "Monthly updates, maintenance, and support"],
    cta: "Launch My Site", ctaSymbol: "→", stripeEnv: "REACT_APP_STRIPE_DEV_PKG_A",
  },
  {
    tier: "03", glyph: "◈", name: "Architect", price: "$1,200", cadence: "/ month", tag: "Full stack", featured: false,
    description: "Full-stack applications designed for teams ready to build something that scales. Dashboards, portals, SaaS products — engineered with the architecture your growth demands.",
    includes: ["Full-stack web application development", "Custom backend, APIs, and database architecture", "Authentication, roles, and access controls", "Third-party integrations and data pipelines", "Performance optimization and scaling strategy", "Dedicated engineering and ongoing support"],
    cta: "Build My Application", ctaSymbol: "→", stripeEnv: "REACT_APP_STRIPE_DEV_PKG_B",
  },
  {
    tier: "04", glyph: "◫", name: "Omni Platform", price: "From $3,500", cadence: "/ month", tag: "Multi-platform", featured: false,
    description: "Web, iOS, and Android from a unified codebase. Purpose-built for serious teams who need cross-platform reach without the overhead of managing three separate projects.",
    includes: ["Cross-platform web, iOS, and Android", "Unified backend and API layer", "App Store and Play Store submission", "Push notifications and offline support", "Dedicated project management throughout"],
    cta: "Let's Talk Platform", ctaSymbol: "↗", stripeEnv: "REACT_APP_STRIPE_DEV_PKG_C",
  },
];

function WebDevCard({ plan }) {
  const { checkout, loading } = useStripeCheckout();
  const [hovered, setHovered] = useState(false);
  const featured = plan.featured;
  const priceKey = plan.stripeEnv ? plan.stripeEnv.replace(/^REACT_APP_/, "") : null;

  const cardBg = featured
    ? "linear-gradient(160deg, #232838 0%, #181C28 100%)"
    : "linear-gradient(160deg, #1B1E26 0%, #14161D 100%)";

  return (
    <div
      className={`pricing-card-shimmer-border${featured ? " featured" : ""}`}
      style={{
        background: cardBg,
        border: `1px solid ${featured ? "rgba(157,180,202,0.35)" : "rgba(58,64,81,0.4)"}`,
        padding: "32px 24px 32px",
        display: "flex", flexDirection: "column",
        transition: `all ${MOTION.medium} ${MOTION.ease}`,
        boxShadow: hovered ? (featured ? "0 0 30px rgba(127,161,194,0.2)" : GLOW.card) : "none",
        transform: hovered ? "translateY(-2px)" : "translateY(0)",
        position: "relative", overflow: "hidden",
        minHeight: 380,
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className={featured ? "pricing-card-hatch-featured" : "pricing-card-hatch"} />
      {featured && <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "2px", background: `linear-gradient(90deg, transparent, rgba(127,161,194,0.6), transparent)` }} />}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28, position: "relative", zIndex: 1 }}>
        <span style={{ fontFamily: "var(--font-mono)", fontSize: "9px", letterSpacing: "0.3em", color: PALETTE.teal700 }}>{plan.tier}</span>
        <span style={{
          fontFamily: "var(--font-mono)", fontSize: "9px", letterSpacing: "0.15em", textTransform: "uppercase",
          color: featured ? PALETTE.teal300 : PALETTE.teal500,
          border: `1px solid ${featured ? "rgba(157,180,202,0.4)" : "rgba(58,64,81,0.5)"}`,
          padding: "3px 9px",
          background: featured ? "rgba(127,161,194,0.08)" : "transparent",
        }}>
          {plan.tag}
        </span>
      </div>

      <div style={{ fontSize: "20px", color: featured ? PALETTE.teal400 : PALETTE.teal600, marginBottom: 14, position: "relative", zIndex: 1 }}>{plan.glyph}</div>
      <h3 style={{ fontFamily: "var(--font-display)", fontWeight: 400, fontSize: "22px", lineHeight: 1.15, color: PALETTE.white, marginBottom: 12, position: "relative", zIndex: 1 }}>{plan.name}</h3>
      <div style={{ marginBottom: 20, position: "relative", zIndex: 1 }}>
        <span style={{ fontFamily: "var(--font-display)", fontWeight: 300, fontStyle: "italic", fontSize: "32px", color: featured ? PALETTE.teal300 : PALETTE.white, letterSpacing: "-0.02em" }}>{plan.price}</span>
        <span style={{ fontFamily: "var(--font-mono)", fontSize: "10px", letterSpacing: "0.15em", color: PALETTE.teal700, marginLeft: 6 }}>{plan.cadence}</span>
      </div>
      <div style={{ height: "1px", background: `linear-gradient(90deg, ${featured ? "rgba(157,180,202,0.3)" : "rgba(58,64,81,0.4)"}, transparent)`, marginBottom: 20 }} />
      <p style={{ fontFamily: "var(--font-body)", fontWeight: 300, fontSize: "13px", lineHeight: 1.75, color: PALETTE.whiteMid, marginBottom: 24, flexGrow: 1, position: "relative", zIndex: 1 }}>{plan.description}</p>
      <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 7, position: "relative", zIndex: 1 }}>
        {plan.includes.map((item, i) => (
          <li key={i} style={{ fontFamily: "var(--font-body)", fontWeight: 300, fontSize: "12px", lineHeight: 1.6, color: PALETTE.whiteMid, display: "flex", alignItems: "flex-start", gap: 8 }}>
            <span style={{ color: PALETTE.teal500, flexShrink: 0, marginTop: 2, fontSize: 10 }}>◌</span>
            {item}
          </li>
        ))}
      </ul>
      <button onClick={() => priceKey && checkout(priceKey)} disabled={loading || !priceKey} className={`pricing-cta-btn${featured ? " featured" : ""}`} style={{ marginTop: 28 }}>
        <span>{loading ? "Redirecting..." : plan.cta}</span>
        <span aria-hidden="true">{plan.ctaSymbol}</span>
      </button>
    </div>
  );
}

function WebDevServices() {
  const ref = useRef();
  const p   = useScrollProgress(ref);
  const t   = useRAF();

  return (
    <section
      ref={ref}
      id="web-dev"
      data-testid="webdev-services"
      className="section-pad"
      style={{ background: "#171A21", position: "relative", overflow: "hidden" }}>
      <div
        aria-hidden="true"
        style={{
          position: "absolute", top: "25%", left: "-5%",
          width: "450px", height: "450px",
          background: `radial-gradient(circle, rgba(127,161,194,0.04), transparent 65%)`,
          pointerEvents: "none",
        }}
      />

      <div className="section-header-2col gsap-reveal">
        <div>
          <p className="teal-label" style={{ marginBottom: 20 }}>◫ &nbsp; Web &amp; Application Development</p>
          <h2 style={{
            fontFamily: "var(--font-display)", fontWeight: 300,
            fontSize: "clamp(32px, 4vw, 56px)", lineHeight: 1,
            letterSpacing: "-0.01em", color: PALETTE.white,
          }}>
            Platforms Built<br />
            <em style={{ color: PALETTE.teal400, textShadow: GLOW.text }}>to Last.</em>
          </h2>
        </div>
        <div>
          <p style={{
            fontFamily: "var(--font-body)", fontWeight: 300, fontSize: "17px",
            lineHeight: 1.85, color: PALETTE.whiteMid, marginBottom: 24,
          }}>
            We don't produce generic deliverables. Every platform we build is
            an original work of engineering — designed for your specific context,
            optimized for your specific audience, and architected to grow with you.
            Our clients come for the craft. They stay because the results speak.
          </p>
          <p style={{
            fontFamily: "var(--font-display)", fontStyle: "italic", fontWeight: 400,
            fontSize: "18px", color: PALETTE.teal400,
            borderLeft: `2px solid ${PALETTE.teal700}`, paddingLeft: "20px",
            textShadow: GLOW.text,
          }}>
            Your vision, realized at the level it deserves.
          </p>
        </div>
      </div>

      <div
        className="diagram-frame gsap-reveal"
        style={{
          opacity: Math.min(1, p * 1.5),
          transform: `translateY(${(1 - Math.min(1, p * 1.5)) * 36}px)`,
          transition: "opacity 0.5s ease, transform 0.5s ease",
        }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 24 }}>
          <span className="mono-tiny" style={{ color: PALETTE.teal500 }}>FIG. 02 · PLATFORM ARCHITECTURE</span>
          <span className="mono-tiny" style={{ color: PALETTE.teal600 }}>SCALE 1:1 &nbsp; ◌ &nbsp; LIVE</span>
        </div>
        <div style={{ height: 360 }}>
          <ArchitectureDiagram width={1200} height={360} t={t} ink={PALETTE.diagramInk} accent={PALETTE.teal400} />
        </div>
      </div>

      <div style={{ position: "relative", marginBottom: 80 }}>
        <div className="pricing-grid gsap-reveal" style={{ position: "relative", zIndex: 1 }}>
          {webDevPlans.map((plan) => (
            <WebDevCard key={plan.tier} plan={plan} />
          ))}
        </div>
      </div>

      <div className="cta-band gsap-reveal">
        <div>
          <p className="teal-label" style={{ marginBottom: 16 }}>◈ &nbsp; Ready to Build Something That Lasts?</p>
          <p style={{
            fontFamily: "var(--font-body)", fontWeight: 300, fontSize: "15px",
            lineHeight: 1.75, color: PALETTE.whiteMid, maxWidth: "480px",
          }}>
            Whether it's a high-converting web presence or a complex cross-platform product,
            we bring the same standard of craft to every engagement.
            Start with a strategy call or step directly into your build.
          </p>
        </div>
        <div className="cta-band-buttons">
          <WebDevCTAButtons />
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   APS — ADVANCED PLATFORM SERVICES
───────────────────────────────────────────── */
const apsTiers = [
  {
    id: "discovery", badge: "Phase 01", glyph: "◎", tag: "Foundation",
    title: "Discovery & Architecture",
    description: "Every serious platform begins with a serious plan. We map your operations, analyze compliance requirements, and design the infrastructure blueprint before a single line of code is written.",
    points: ["Compliance-oriented systems analysis", "Infrastructure architecture blueprint", "Workflow and integration mapping", "Risk reduction and scalability planning", "Stakeholder alignment and technical roadmap"],
    cta: "Begin Discovery", ctaSymbol: "↗",
  },
  {
    id: "build", badge: "Phase 02 – 05", glyph: "◈", tag: "Core Build",
    title: "Platform Development",
    description: "From MVP to full enterprise deployment — security-first architecture, encrypted data workflows, and hardened infrastructure built to operate under real-world conditions.",
    points: ["HIPAA-conscious platform architecture", "Encrypted data handling and secure pipelines", "Role-based access, MFA, and audit logging", "Compliance controls and governance frameworks", "Cross-platform and multi-location deployment", "Penetration testing cadence and vulnerability disclosure policy"],
    cta: "Explore Development", ctaSymbol: "↗",
  },
  {
    id: "operations", badge: "Ongoing", glyph: "◉", tag: "Operations",
    title: "Platform Operations",
    description: "Sustained infrastructure support, security oversight, and platform evolution — so your system stays as strong on day 1,000 as it did on day one.",
    points: ["Infrastructure monitoring and incident response", "Security patch management and hardening", "Performance optimization and capacity planning", "Feature enhancements and architectural evolution", "Dedicated operational support team", "SOC 2-aligned operational practices"],
    cta: "Discuss Operations", ctaSymbol: "↗",
  },
];

function APSCard({ tier, hovered, setHovered }) {
  return (
    <div
      className="pricing-card-shimmer-border"
      style={{
        background: "linear-gradient(160deg, #1B1E26 0%, #14161D 100%)",
        border: `1px solid ${hovered ? "rgba(157,180,202,0.28)" : "rgba(58,64,81,0.45)"}`,
        padding: "32px 28px 32px",
        display: "flex", flexDirection: "column",
        transition: `all ${MOTION.medium} ${MOTION.ease}`,
        boxShadow: hovered ? GLOW.card : "none",
        transform: hovered ? "translateY(-3px)" : "translateY(0)",
        position: "relative", overflow: "hidden",
        minHeight: 440,
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}>
      <div style={{
        position: "absolute", inset: 0, pointerEvents: "none",
        backgroundImage: "repeating-linear-gradient(-45deg, transparent 0px, transparent 14px, rgba(127,161,194,0.03) 14px, rgba(127,161,194,0.03) 15px)",
      }} />
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0, height: "2px",
        background: `linear-gradient(90deg, transparent, ${hovered ? "rgba(127,161,194,0.5)" : "transparent"}, transparent)`,
        transition: `all ${MOTION.medium} ease`,
      }} />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28, position: "relative", zIndex: 1 }}>
        <span style={{
          fontFamily: "var(--font-mono)", fontSize: "9px", letterSpacing: "0.2em",
          textTransform: "uppercase", color: PALETTE.teal500,
          border: "1px solid rgba(58,64,81,0.5)", padding: "4px 10px",
          background: "rgba(127,161,194,0.05)",
        }}>
          {tier.badge}
        </span>
        <span style={{
          fontFamily: "var(--font-mono)", fontSize: "9px", letterSpacing: "0.25em",
          color: PALETTE.teal700, textTransform: "uppercase",
        }}>
          {tier.tag}
        </span>
      </div>
      <div style={{ fontSize: "22px", color: hovered ? PALETTE.teal400 : PALETTE.teal600, marginBottom: 16, position: "relative", zIndex: 1, transition: `color ${MOTION.fast} ease` }}>
        {tier.glyph}
      </div>
      <h3 style={{
        fontFamily: "var(--font-display)", fontWeight: 400, fontSize: "clamp(20px, 2vw, 26px)",
        lineHeight: 1.1, color: hovered ? PALETTE.white : "rgba(240,241,244,0.9)",
        marginBottom: 20, position: "relative", zIndex: 1, transition: `color ${MOTION.fast} ease`,
      }}>
        {tier.title}
      </h3>
      <div style={{ height: "1px", background: "linear-gradient(90deg, rgba(58,64,81,0.5), transparent)", marginBottom: 20 }} />
      <p style={{
        fontFamily: "var(--font-body)", fontWeight: 300, fontSize: "13px",
        lineHeight: 1.8, color: PALETTE.whiteMid, marginBottom: 24,
        flexGrow: 1, position: "relative", zIndex: 1,
      }}>
        {tier.description}
      </p>
      <ul style={{ listStyle: "none", padding: 0, margin: "0 0 28px", display: "flex", flexDirection: "column", gap: 8, position: "relative", zIndex: 1 }}>
        {tier.points.map((pt, i) => (
          <li key={i} style={{ fontFamily: "var(--font-body)", fontWeight: 300, fontSize: "12px", lineHeight: 1.6, color: PALETTE.whiteMid, display: "flex", alignItems: "flex-start", gap: 8 }}>
            <span style={{ color: PALETTE.teal500, flexShrink: 0, fontSize: 10, marginTop: 2 }}>◌</span>
            {pt}
          </li>
        ))}
      </ul>
      <a
        href="https://advanced.digitalbillionsgroup.com"
        target="_blank"
        rel="noopener noreferrer"
        className="pricing-cta-btn"
        style={{ position: "relative", zIndex: 1 }}>
        <span>{tier.cta}</span>
        <span aria-hidden="true">{tier.ctaSymbol}</span>
      </a>
    </div>
  );
}

function APSSection() {
  const [hoveredCard, setHoveredCard] = useState(null);
  return (
    <section id="aps" className="aps-section section-pad" style={{ position: "relative", overflow: "hidden" }}>
      <div
        aria-hidden="true"
        style={{
          position: "absolute", inset: 0, zIndex: 0, pointerEvents: "none",
          backgroundImage: `url(${starsImg})`, backgroundSize: "cover",
          backgroundPosition: "center top", opacity: 0.06, mixBlendMode: "screen",
        }}
      />
      <div
        aria-hidden="true"
        style={{
          position: "absolute", top: "5%", left: "50%", transform: "translateX(-50%)",
          width: "80%", height: "500px",
          background: `radial-gradient(ellipse at center, rgba(127,161,194,0.05), transparent 65%)`,
          pointerEvents: "none",
        }}
      />
      <ParticleField style={{ opacity: 0.2 }} />
      <VignetteOverlay strength={0.55} color="#171A21" />

      <div style={{ position: "relative", zIndex: 2 }}>
        <div className="gsap-reveal" style={{ textAlign: "center", marginBottom: 80 }}>
          <p className="teal-label" style={{ marginBottom: 20 }}>
            ◈ &nbsp; Advanced Platform Services &nbsp; ◈
          </p>
          <GlowLine style={{ margin: "0 auto 40px", maxWidth: 120 }} animated />
          <h2 style={{
            fontFamily: "var(--font-display)", fontWeight: 300,
            fontSize: "clamp(32px, 5vw, 72px)", lineHeight: 0.95,
            letterSpacing: "-0.02em", color: PALETTE.white, marginBottom: 24,
          }}>
            For Organizations Where<br />
            <em style={{ color: PALETTE.teal400, textShadow: GLOW.text }}>
              Infrastructure Is Mission-Critical.
            </em>
          </h2>
          <p style={{
            fontFamily: "var(--font-body)", fontWeight: 300, fontSize: "18px",
            lineHeight: 1.75, color: PALETTE.whiteMid, maxWidth: "640px", margin: "0 auto 20px",
          }}>
            Patient records. Regulated data pipelines. Systems that cannot go down.
            Our Advanced Platform Services team exists precisely for these engagements.
          </p>
          <p style={{
            fontFamily: "var(--font-body)", fontWeight: 300, fontSize: "16px",
            lineHeight: 1.75, color: PALETTE.whiteSoft, maxWidth: "560px", margin: "0 auto",
          }}>
            Healthcare organizations, compliance-driven enterprises, and
            multi-location platforms work with our APS team for end-to-end
            infrastructure design, HIPAA-conscious architecture, and the kind of
            sustained operational support that regulated environments demand.
          </p>
        </div>

        <div
          className="gsap-reveal stagger-container"
          style={{
            display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 1,
            background: PALETTE.border, border: `1px solid ${PALETTE.border}`, marginBottom: 80,
          }}>
          {[
            {
              icon: "◉",
              label: "Security-First Architecture",
              desc: "Systems built with administrative, technical, and operational safeguards from the ground up — including penetration testing cadences and vulnerability disclosure policies.",
            },
            {
              icon: "◈",
              label: "Scalable, Compliant Infrastructure",
              desc: "Multi-location deployments, white-label frameworks, and enterprise ecosystems designed to meet regulatory standards without sacrificing performance.",
            },
            {
              icon: "▫",
              label: "SOC 2-Aligned Operations",
              desc: "Ongoing platform support governed by SOC 2-aligned practices — security, availability, and confidentiality at the center of everything we maintain.",
            },
          ].map((f, i) => (
            <div
              key={i}
              className="stagger-child"
              style={{
                background: "#1B1E26", padding: "40px 32px",
                borderTop: "2px solid transparent",
                transition: `all ${MOTION.medium} ${MOTION.ease}`,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#262B36";
                e.currentTarget.style.borderTopColor = PALETTE.teal600;
                e.currentTarget.style.boxShadow = "0 0 24px rgba(127,161,194,0.08)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "#1B1E26";
                e.currentTarget.style.borderTopColor = "transparent";
                e.currentTarget.style.boxShadow = "none";
              }}>
              <div style={{ fontSize: 24, color: PALETTE.teal500, marginBottom: 16 }}>{f.icon}</div>
              <p className="teal-label" style={{ marginBottom: 12, color: PALETTE.teal400 }}>{f.label}</p>
              <p style={{
                fontFamily: "var(--font-body)", fontWeight: 300, fontSize: "14px",
                lineHeight: 1.75, color: PALETTE.whiteMid,
              }}>
                {f.desc}
              </p>
            </div>
          ))}
        </div>

        {/* APS Tier Cards */}
        <div
          style={{
            display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "1px",
            background: PALETTE.border, border: `1px solid ${PALETTE.border}`, marginBottom: 60,
          }}
          className="gsap-reveal">
          {apsTiers.map((tier, i) => (
            <APSCard
              key={tier.id}
              tier={tier}
              hovered={hoveredCard === i}
              setHovered={(v) => setHoveredCard(v ? i : null)}
            />
          ))}
        </div>

        <div className="cta-band gsap-reveal" style={{ marginTop: 60 }}>
          <div>
            <p className="teal-label" style={{ marginBottom: 16 }}>◈ &nbsp; Speak with the APS Team</p>
            <p style={{
              fontFamily: "var(--font-body)", fontWeight: 300, fontSize: "15px",
              lineHeight: 1.75, color: PALETTE.whiteMid, maxWidth: "480px",
            }}>
              Advanced Platform Services engagements begin with a strategic discovery
              session. If your organization operates in a regulated environment or requires
              infrastructure built to withstand scrutiny — we're ready for that conversation.
            </p>
          </div>
          <div className="cta-band-buttons">
            <a href="https://advanced.digitalbillionsgroup.com" target="_blank" rel="noopener noreferrer" className="btn-teal">
              <span>Visit APS Portal</span>
              <span aria-hidden="true">↗</span>
            </a>
            <a href="mailto:hello@digitalbillionsgroup.com" className="btn-outline">
              <span>Contact APS Team</span>
              <span aria-hidden="true">→</span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   CONTACT + FOOTER
───────────────────────────────────────────── */
function Contact() {
  return (
    <section
      id="contact"
      data-testid="contact"
      style={{
        background: `linear-gradient(160deg, #171A21 0%, ${PALETTE.ink} 100%)`,
        position: "relative", overflow: "hidden",
        borderTop: `1px solid ${PALETTE.border}`,
      }}>
      <div
        aria-hidden="true"
        style={{
          position: "absolute", bottom: "20%", right: "5%",
          width: "480px", height: "480px",
          background: `radial-gradient(circle, rgba(127,161,194,0.06), transparent 65%)`,
          pointerEvents: "none",
        }}
      />
      <div
        aria-hidden="true"
        style={{
          position: "absolute", top: "15%", left: "3%",
          width: "320px", height: "320px",
          background: `radial-gradient(circle, rgba(74,113,145,0.04), transparent 65%)`,
          pointerEvents: "none",
        }}
      />
      <div style={{ position: "absolute", top: 32, left: 32, opacity: 0.3, zIndex: 2 }}>
        <BlueprintCorner position="tl" ink={PALETTE.teal700} size={32} />
      </div>
      <div style={{ position: "absolute", top: 32, right: 32, opacity: 0.3, zIndex: 2 }}>
        <BlueprintCorner position="tr" ink={PALETTE.teal700} size={32} />
      </div>
      <VignetteOverlay strength={0.45} color={PALETTE.ink} />

      <div className="section-pad" style={{ position: "relative", zIndex: 2 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "80px", alignItems: "start" }}>
          {/* Left — editorial */}
          <div className="gsap-reveal">
            <p className="teal-label" style={{ marginBottom: 20 }}>◈ &nbsp; Where It Begins</p>
            <GlowLine style={{ marginBottom: 36, maxWidth: 48 }} />
            <h2 style={{
              fontFamily: "var(--font-display)", fontWeight: 300,
              fontSize: "clamp(36px, 5.5vw, 80px)", lineHeight: 0.92,
              letterSpacing: "-0.025em", color: PALETTE.white, marginBottom: 36,
            }}>
              Every platform<br />
              starts with a<br />
              <em style={{ color: PALETTE.teal400, textShadow: GLOW.text }}>real conversation.</em>
            </h2>
            <p style={{
              fontFamily: "var(--font-body)", fontWeight: 300, fontSize: "17px",
              lineHeight: 1.85, color: PALETTE.whiteMid, maxWidth: "440px", marginBottom: 40,
            }}>
              Before we write a line of code, we listen. Your goals, your constraints,
              your vision — we need to understand all of it. That's not courtesy.
              That's how great work starts.
            </p>
            <p style={{
              fontFamily: "var(--font-display)", fontStyle: "italic", fontWeight: 400,
              fontSize: "18px", lineHeight: 1.6, color: PALETTE.teal400,
              borderLeft: `2px solid ${PALETTE.teal700}`, paddingLeft: "20px",
              textShadow: GLOW.text,
            }}>
              Understanding first. Everything else follows.
            </p>
            <div style={{ display: "flex", gap: 16, marginTop: 44, flexWrap: "wrap" }}>
              <a href="mailto:hello@digitalbillionsgroup.com" className="btn-teal">
                <span>Start a Conversation</span>
                <span aria-hidden="true">↗</span>
              </a>
              <a href="https://advanced.digitalbillionsgroup.com" target="_blank" rel="noopener noreferrer" className="btn-outline">
                <span>APS Enterprise</span>
                <span aria-hidden="true">◈</span>
              </a>
            </div>
          </div>

          {/* Right — contact details */}
          <div className="gsap-reveal" data-reveal-delay="150">
            <svg width="100%" height="140" viewBox="0 0 480 140" style={{ marginBottom: 48, opacity: 0.35 }}>
              <line x1="0" y1="70" x2="480" y2="70" stroke={PALETTE.teal700} strokeWidth="0.5" />
              {[60, 120, 180, 240, 300, 360, 420].map((x, i) => (
                <g key={i}>
                  <line x1={x} y1="60" x2={x} y2="80" stroke={PALETTE.teal700} strokeWidth="0.5" />
                  <circle cx={x} cy="70" r={i === 3 ? 4 : 2} fill={i === 3 ? PALETTE.teal500 : "none"} stroke={PALETTE.teal700} strokeWidth="0.5" />
                </g>
              ))}
              <text x="240" y="24" textAnchor="middle" style={{ fontFamily: "var(--font-mono)", fontSize: "9px", letterSpacing: "0.3em", fill: PALETTE.teal700 }}>
                COMM NODE ◌ ACTIVE
              </text>
              <text x="240" y="120" textAnchor="middle" style={{ fontFamily: "var(--font-mono)", fontSize: "9px", letterSpacing: "0.3em", fill: PALETTE.teal800 }}>
                DBG ◌ GLOBAL ◌ MMXXVI
              </text>
            </svg>

            <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
              <div style={{ paddingBottom: 36, borderBottom: `1px solid ${PALETTE.border}`, marginBottom: 36 }}>
                <p className="teal-label" style={{ marginBottom: 14 }}>✉ &nbsp; All Inquiries</p>
                <a
                  href="mailto:hello@digitalbillionsgroup.com"
                  style={{
                    fontFamily: "var(--font-display)", fontWeight: 300,
                    fontSize: "clamp(16px, 2vw, 24px)", color: PALETTE.white,
                    textDecoration: "none", transition: `all ${MOTION.fast} ease`, display: "block",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = PALETTE.teal400; e.currentTarget.style.textShadow = GLOW.text; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = PALETTE.white; e.currentTarget.style.textShadow = "none"; }}>
                  hello@digitalbillionsgroup.com
                </a>
              </div>

              <div style={{ paddingBottom: 36, borderBottom: `1px solid ${PALETTE.border}`, marginBottom: 36 }}>
                <p className="teal-label" style={{ marginBottom: 14 }}>◈ &nbsp; APS Enterprise Portal</p>
                <a
                  href="https://advanced.digitalbillionsgroup.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    fontFamily: "var(--font-mono)", fontSize: "13px", letterSpacing: "0.12em",
                    color: PALETTE.teal500, textDecoration: "none",
                    transition: `color ${MOTION.fast} ease`, display: "block",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = PALETTE.teal300; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = PALETTE.teal500; }}>
                  advanced.digitalbillionsgroup.com ↗
                </a>
              </div>

              <div style={{ paddingBottom: 36, borderBottom: `1px solid ${PALETTE.border}`, marginBottom: 36 }}>
                <p className="teal-label" style={{ marginBottom: 14 }}>◌ &nbsp; How We Engage</p>
                <p style={{
                  fontFamily: "var(--font-body)", fontWeight: 300, fontSize: "14px",
                  lineHeight: 1.8, color: PALETTE.whiteMid,
                }}>
                  Every inquiry receives a personal response from our team —
                  not a template, not a bot. We respond because your project
                  deserves to be understood before it's proposed.
                </p>
              </div>

              <div>
                <p style={{
                  fontFamily: "var(--font-mono)", fontSize: "9px", letterSpacing: "0.3em",
                  textTransform: "uppercase", color: PALETTE.whiteFaint, lineHeight: 2.4,
                }}>
                  Digital Billions Group<br />
                  Worldwide · Online · Always Available.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer stripe */}
      <div
        data-testid="footer"
        style={{
          borderTop: `1px solid ${PALETTE.border}`,
          padding: "24px var(--pad-x)",
          display: "flex", justifyContent: "space-between", alignItems: "center",
          position: "relative", zIndex: 2,
          background: "linear-gradient(180deg, transparent, rgba(13,15,20,0.6))",
        }}>
        <div className="footer-shimmer-line" aria-hidden="true" />
        <span style={{
          fontFamily: "var(--font-mono)", fontSize: "9px", letterSpacing: "0.3em",
          textTransform: "uppercase", color: PALETTE.teal300,
        }}>
          ◈ DIGITAL BILLIONS GROUP &nbsp; ◌ &nbsp; MMXXII—MMXXVI
        </span>
        <div style={{ display: "flex", gap: 32, alignItems: "center" }}>
          <span style={{
            fontFamily: "var(--font-mono)", fontSize: "9px", letterSpacing: "0.2em",
            textTransform: "uppercase", color: PALETTE.teal300,
          }}>
            VISION · INFRASTRUCTURE · IMPACT
          </span>
          <div style={{ display: "flex", gap: 20, alignItems: "center" }}>
            <a href="mailto:hello@digitalbillionsgroup.com" className="nav-link footer-link">
              Contact
            </a>
            <a href="/terms" className="nav-link footer-link">
              Terms
            </a>
            <a href="/privacy" className="nav-link footer-link">
              Privacy
            </a>
            <a href="https://aps.digitalbillionsgroup.com" target="_blank" rel="noopener noreferrer" className="nav-link footer-link">
              APS ↗
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer() { return null; }

/* ─────────────────────────────────────────────
   APP
───────────────────────────────────────────── */
function App() {
  const [bootDone, setBootDone] = useState(false);
  useLenis();
  useGSAPReveal('.gsap-reveal');
  useStaggerReveal('.stagger-container');
  return (
    <>
      {!bootDone && <BootSequence onComplete={() => setBootDone(true)} />}
      <NoiseTexture opacity={0.03} />
      <ScrollProgress />
      <Nav />
      <main data-testid="main-wrapper">
        <Hero />
        <About />
        <Services />
        <AtmosphericDivider />
        <AIServices />
        <WebDevServices />
        <APSSection />
        <Contact />
      </main>
      <Footer />
    </>
  );
}

export default App;
